import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, RefreshCw, BarChart3 } from "lucide-react";

import { MarketChart } from "@/components/charts/MarketChart";
import type { WatchlistSortState } from "@/components/grid/WatchlistGrid";
import { GlobalAppMenu } from "@/components/nav/GlobalAppMenu";
import { SymbolSearchInput } from "@/components/symbol/SymbolSearchInput";
import { AddIntoWatchlistModal } from "@/components/watchlist/AddIntoWatchlistModal";
import { WatchlistWorkspace } from "@/components/watchlist/WatchlistWorkspace";
import { useBarsQuery, useBoardsQuery, useBootstrapQuery, useFactorsQuery, useFundamentalsQuery } from "@/features/market-data/queries";
import { normalizeTerminalSymbol } from "@/lib/symbol";
import { useWatchlistItemsSortedQuery, useWatchlistsQuery } from "@/features/watchlist/queries";
import { formatCompact, formatNumber } from "@/lib/utils";
import { apiClient } from "@/services/api/client";
import type { WatchlistItem } from "@/services/api/types";
import { RealtimeClient } from "@/services/websocket/client";
import { useUiStore } from "@/stores/ui-store";

const marketRealtime = new RealtimeClient("/ws/market");

const INTERVAL_OPTIONS = ["1", "5", "15", "30", "60", "D", "W", "M"] as const;
const ADJUST_OPTIONS = ["none", "qfq", "hfq"] as const;
const INDICATORS = ["ma", "boll", "macd", "kdj"] as const;
const INITIAL_BAR_LIMIT = 500;
const EDGE_FETCH_LIMIT = 100;
const TERMINAL_SESSION_STORAGE_KEY = "lisaq:terminal-session";
const BARS_DEBUG_ENABLED = true;

function barsDebugLog(event: string, payload: Record<string, unknown>) {
  if (!BARS_DEBUG_ENABLED || typeof window === "undefined") {
    return;
  }
  const entry = {
    ts: new Date().toISOString(),
    event: `bars_${event}`,
    ...payload,
  };
  const debugWindow = window as unknown as { __lisaqZoomDebugEvents?: Array<Record<string, unknown>> };
  if (!Array.isArray(debugWindow.__lisaqZoomDebugEvents)) {
    debugWindow.__lisaqZoomDebugEvents = [];
  }
  debugWindow.__lisaqZoomDebugEvents.push(entry);
  if (debugWindow.__lisaqZoomDebugEvents.length > 1200) {
    debugWindow.__lisaqZoomDebugEvents.shift();
  }
  console.debug("[lisaq-bars]", entry);
}

interface TerminalBar {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface LiveQuoteSnapshot {
  close: number | null;
  pct_chg: number | null;
  pre_close: number | null;
  change: number | null;
  trade_date: string | null;
  vol: number | null;
  amount: number | null;
  source: string | null;
}

interface TerminalSessionState {
  interval: (typeof INTERVAL_OPTIONS)[number];
  adjust: (typeof ADJUST_OPTIONS)[number];
  indicators: string[];
  layoutPreset: string;
  leftPanel: string;
  rightPanel: string;
  watchlistView: "compact" | "table";
}

export function MarketTerminalPage() {
  const search = useSearch({ from: "/stock" });
  const navigate = useNavigate({ from: "/stock" });
  const queryClient = useQueryClient();
  const activeBarsDatasetRef = useRef<string | null>(null);
  const sessionHydratedRef = useRef(false);
  const profileSettingsRef = useRef<Record<string, unknown>>({});
  const saveTimerRef = useRef<number | null>(null);
  const [searchInput, setSearchInput] = useState(search.symbol);
  const [watchlistView, setWatchlistView] = useState<"compact" | "table">("table");
  const [addIntoWatchlistOpen, setAddIntoWatchlistOpen] = useState(false);
  const [symbolLiveQuote, setSymbolLiveQuote] = useState<LiveQuoteSnapshot | null>(null);
  const [chartState, setChartState] = useState<{ bars: TerminalBar[]; hasMoreBefore: boolean; hasMoreAfter: boolean }>({
    bars: [],
    hasMoreBefore: false,
    hasMoreAfter: false,
  });

  const bootstrapQuery = useBootstrapQuery();
  const watchlistsQuery = useWatchlistsQuery();
  const watchlistsData = watchlistsQuery.data || bootstrapQuery.data?.watchlists;
  const watchlistMeta = watchlistsData?.watchlists || [];
  const maxListsPerUser = watchlistsData?.max_lists_per_user ?? 100;
  const selectedWatchlistId = search.watchlistId ?? watchlistsData?.default_watchlist_id ?? watchlistMeta[0]?.id ?? null;
  const currentWatchlist =
    watchlistMeta.find((item) => Number(item.id) === Number(selectedWatchlistId)) ||
    watchlistMeta[0] ||
    null;
  const currentSortState: WatchlistSortState = useMemo(
    () => ({
      field: normalizeWatchlistSortField(currentWatchlist?.sort_by || "pct_chg"),
      direction: currentWatchlist?.sort_dir === "asc" ? "asc" : "desc",
    }),
    [currentWatchlist?.sort_by, currentWatchlist?.sort_dir],
  );
  const watchlistItemsQuery = useWatchlistItemsSortedQuery(selectedWatchlistId, {
    sortBy: currentSortState.field,
    sortDir: currentSortState.direction,
    limit: 300,
  });
  const barsQuery = useBarsQuery(search.symbol, search.interval, search.adjust, INITIAL_BAR_LIMIT);
  const fundamentalsQuery = useFundamentalsQuery(search.symbol);
  const factorsQuery = useFactorsQuery(search.symbol);
  const boardsQuery = useBoardsQuery(search.symbol);

  const { marketConnection, apiLatencyMs, lastMarketUpdate, setApiLatencyMs, setLastMarketUpdate, setMarketConnection } = useUiStore();

  const currentUser = bootstrapQuery.data?.current_user;
  const watchRows = watchlistItemsQuery.data?.items || [];
  const boardItems = boardsQuery.data?.boards?.items || boardsQuery.data?.items || [];
  const fundamentals = fundamentalsQuery.data?.fundamentals || fundamentalsQuery.data || {};
  const factorSummary = factorsQuery.data?.scores || factorsQuery.data || {};
  const activeWatchItem = watchRows.find((item) => item.symbol === search.symbol) || null;
  const displayName = activeWatchItem?.display_name || activeWatchItem?.stock_name || activeWatchItem?.name_cache || search.symbol;
  const percentChange = symbolLiveQuote?.pct_chg ?? activeWatchItem?.pct_chg ?? null;
  const absoluteChange =
    symbolLiveQuote?.change ??
    activeWatchItem?.change ??
    (percentChange != null && (symbolLiveQuote?.pre_close ?? activeWatchItem?.pre_close) != null
      ? (symbolLiveQuote?.pre_close ?? activeWatchItem?.pre_close ?? 0) * (percentChange / 100)
      : null);
  const isUp = (percentChange ?? 0) >= 0;
  const lastBar = chartState.bars[chartState.bars.length - 1];
  const priceValue = symbolLiveQuote?.close ?? activeWatchItem?.close ?? lastBar?.close;
  const tradeDateValue = symbolLiveQuote?.trade_date ?? activeWatchItem?.trade_date ?? lastBar?.time ?? null;
  const displayUserName = currentUser?.display_name || currentUser?.email || "Guest";
  const barsDatasetKey = `${search.symbol}:${search.interval}:${search.adjust}`;

  useEffect(() => {
    return () => {
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const onLatency = (event: Event) => {
      const detail = (event as CustomEvent<number>).detail;
      setApiLatencyMs(detail);
    };
    window.addEventListener("lisaq:api-latency", onLatency as EventListener);
    return () => window.removeEventListener("lisaq:api-latency", onLatency as EventListener);
  }, [setApiLatencyMs]);

  useEffect(() => {
    marketRealtime.connect(
      () => {
        setMarketConnection("connected");
        marketRealtime.send("subscribe", { symbols: [search.symbol], interval: search.interval, adjust: search.adjust });
      },
      () => setMarketConnection("disconnected"),
    );

    const unbindMarket = marketRealtime.subscribe((message) => {
      const typed = message as { type?: string };
      if (typed.type === "quote.snapshot") {
        setLastMarketUpdate(new Date().toLocaleTimeString("zh-CN"));
      }
    });

    return () => {
      unbindMarket();
    };
  }, [search.adjust, search.interval, search.symbol, setLastMarketUpdate, setMarketConnection]);

  useEffect(() => {
    setSearchInput(search.symbol);
  }, [search.symbol]);

  useEffect(() => {
    if (!watchlistMeta.length) {
      return;
    }
    const valid = selectedWatchlistId != null && watchlistMeta.some((item) => Number(item.id) === Number(selectedWatchlistId));
    if (valid) {
      return;
    }
    patchSearch({ watchlistId: Number(watchlistMeta[0]?.id) || null });
  }, [selectedWatchlistId, watchlistMeta]);

  useEffect(() => {
    if (!barsQuery.data) {
      return;
    }
    const incomingBars = normalizeBarsPayload(barsQuery.data.bars);
    setChartState((prev) => {
      if (activeBarsDatasetRef.current !== barsDatasetKey) {
        activeBarsDatasetRef.current = barsDatasetKey;
        return {
          bars: incomingBars,
          hasMoreBefore: Boolean(barsQuery.data?.has_more_before),
          hasMoreAfter: Boolean(barsQuery.data?.has_more_after),
        };
      }
      return {
        bars: mergeBars(prev.bars, incomingBars),
        hasMoreBefore: prev.hasMoreBefore || Boolean(barsQuery.data?.has_more_before),
        hasMoreAfter: prev.hasMoreAfter || Boolean(barsQuery.data?.has_more_after),
      };
    });
  }, [barsDatasetKey, barsQuery.data]);

  useEffect(() => {
    let disposed = false;
    let inflight = false;
    setSymbolLiveQuote(null);

    const tick = async (force = false) => {
      if (disposed || inflight || (!force && document.hidden)) {
        return;
      }
      inflight = true;
      try {
        const payload = await apiClient.getTodayBar(search.symbol, search.adjust, { syncLive: true });
        const normalizedLive = normalizeLiveQuote(payload?.bar, payload?.trade_date);
        if (normalizedLive) {
          setSymbolLiveQuote(normalizedLive);
        }
        const incoming = payload?.bar ? normalizeBarsPayload([payload.bar]) : [];
        if (incoming.length && search.interval === "D") {
          setChartState((prev) => ({
            ...prev,
            bars: mergeBars(prev.bars, incoming),
          }));
        }
        if (payload.source === "tvscreener") {
          setLastMarketUpdate(new Date().toLocaleTimeString("zh-CN"));
        }
      } catch {
        return;
      } finally {
        inflight = false;
      }
    };

    void tick(true);
    const timer = window.setInterval(() => {
      void tick();
    }, 2000);
    const onVisibilityChange = () => {
      if (!document.hidden) {
        void tick(true);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [search.symbol, search.interval, search.adjust, setLastMarketUpdate]);

  useEffect(() => {
    const backendSettings = (bootstrapQuery.data?.watchlists.profile?.settings || {}) as Record<string, unknown>;
    profileSettingsRef.current = backendSettings;

    if (sessionHydratedRef.current || !bootstrapQuery.data) {
      return;
    }
    const storedSession = readTerminalSessionFromLocal();
    const backendSession = parseTerminalSession(backendSettings.terminal_session);
    const session = storedSession || backendSession;
      if (session) {
      setWatchlistView(session.watchlistView);
        navigate({
          search: (prev) => ({
            ...prev,
          interval: session.interval,
          adjust: session.adjust,
          indicators: session.indicators,
          layoutPreset: session.layoutPreset,
          leftPanel: session.leftPanel === "collapsed" ? "collapsed" : "expanded",
          rightPanel: session.rightPanel === "collapsed" ? "collapsed" : "expanded",
        }),
        replace: true,
      });
    }
    sessionHydratedRef.current = true;
  }, [bootstrapQuery.data, navigate]);

  useEffect(() => {
    if (!sessionHydratedRef.current) {
      return;
    }
    const sessionState: TerminalSessionState = {
      interval: search.interval,
      adjust: search.adjust,
      indicators: search.indicators,
      layoutPreset: search.layoutPreset,
      leftPanel: search.leftPanel,
      rightPanel: search.rightPanel,
      watchlistView,
    };
    writeTerminalSessionToLocal(sessionState);

    if (!currentUser) {
      return;
    }
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(() => {
      const mergedSettings = {
        ...profileSettingsRef.current,
        terminal_session: sessionState,
      };
      void apiClient.patchProfileMe({ settings: mergedSettings }).then(() => {
        profileSettingsRef.current = mergedSettings;
      });
    }, 500);
  }, [
    currentUser,
    search.adjust,
    search.indicators,
    search.interval,
    search.layoutPreset,
    search.leftPanel,
    search.rightPanel,
    watchlistView,
  ]);

  function patchSearch(next: Partial<typeof search>) {
    if (!sessionHydratedRef.current) {
      sessionHydratedRef.current = true;
    }
    navigate({
      search: (prev) => ({
        ...prev,
        ...next,
        indicators: Array.isArray(next.indicators) ? next.indicators : prev.indicators,
      }),
      replace: true,
    });
  }

  async function refreshWatchlistQueries() {
    await queryClient.invalidateQueries({ queryKey: ["watchlists"] });
  }

  async function handleCreateWatchlist(name: string) {
    const created = await apiClient.createWatchlist({ name, sort_by: "pct_chg", sort_dir: "desc" });
    await refreshWatchlistQueries();
    patchSearch({ watchlistId: created.id });
  }

  async function handleRenameWatchlist(watchlistId: number, name: string) {
    await apiClient.updateWatchlist(watchlistId, { name });
    await refreshWatchlistQueries();
  }

  async function handleCopyWatchlist(sourceWatchlistId: number, name: string) {
    const source = await apiClient.listWatchlistItems(sourceWatchlistId, { limit: 5000 });
    const created = await apiClient.createWatchlist({
      name,
      sort_by: currentWatchlist?.sort_by || "pct_chg",
      sort_dir: currentWatchlist?.sort_dir === "asc" ? "asc" : "desc",
    });
    if (source.items.length) {
      await apiClient.bulkAddWatchlistItems(
        created.id,
        source.items.map((item) => ({
          symbol: item.symbol,
          instrument_type: item.instrument_type,
          market: item.market ?? undefined,
          alias: item.alias ?? undefined,
          note: item.note ?? undefined,
          name_cache: item.name_cache ?? item.display_name ?? item.stock_name ?? undefined,
          sort_order: item.sort_order,
          pinned: Boolean(item.pinned),
          row_color: item.row_color ?? undefined,
        })),
      );
    }
    await refreshWatchlistQueries();
    patchSearch({ watchlistId: created.id });
  }

  async function handleDeleteWatchlist(watchlistId: number) {
    const deleted = await apiClient.deleteWatchlist(watchlistId);
    await refreshWatchlistQueries();
    const fallbackId = deleted.fallback_watchlist_id ?? watchlistsData?.default_watchlist_id ?? watchlistMeta[0]?.id ?? null;
    patchSearch({ watchlistId: fallbackId });
  }

  async function handleAddItemToWatchlist(symbol: string) {
    if (!selectedWatchlistId) {
      throw new Error("未选择自选股");
    }
    await apiClient.addWatchlistItem(selectedWatchlistId, { symbol });
    await refreshWatchlistQueries();
  }

  async function handleRemoveWatchlistItem(itemId: number) {
    if (!selectedWatchlistId) {
      throw new Error("未选择自选股");
    }
    await apiClient.removeWatchlistItem(selectedWatchlistId, itemId);
    await refreshWatchlistQueries();
  }

  async function handlePatchWatchlistItem(itemId: number, payload: Partial<{ pinned: boolean; row_color: string }>) {
    if (!selectedWatchlistId) {
      throw new Error("未选择自选股");
    }
    await apiClient.patchWatchlistItem(selectedWatchlistId, itemId, payload);
    await refreshWatchlistQueries();
  }

  async function handlePersistSort(nextSort: WatchlistSortState) {
    if (!selectedWatchlistId) {
      return;
    }
    const normalizedField = normalizeWatchlistSortField(nextSort.field);
    if (normalizedField === currentSortState.field && nextSort.direction === currentSortState.direction) {
      return;
    }
    await apiClient.updateWatchlist(selectedWatchlistId, {
      sort_by: normalizedField,
      sort_dir: nextSort.direction,
    });
    await refreshWatchlistQueries();
  }

  async function handleLoadMoreBars(direction: "before" | "after", anchorTime: string) {
    try {
      barsDebugLog("edge_request_start", {
        symbol: search.symbol,
        interval: search.interval,
        adjust: search.adjust,
        direction,
        anchorTime,
      });
      const payload = await apiClient.getBars(search.symbol, search.interval, search.adjust, {
        limit: EDGE_FETCH_LIMIT,
        ...(direction === "before"
          ? { before: toTradeDateDigits(anchorTime) }
          : { after: toTradeDateDigits(anchorTime) }),
      });
      const incomingBars = normalizeBarsPayload(payload.bars);
      barsDebugLog("edge_request_done", {
        symbol: search.symbol,
        interval: search.interval,
        adjust: search.adjust,
        direction,
        anchorTime,
        incoming: incomingBars.length,
        hasMoreBefore: Boolean(payload.has_more_before),
        hasMoreAfter: Boolean(payload.has_more_after),
      });
      if (!incomingBars.length) {
        setChartState((prev) => ({
          ...prev,
          hasMoreBefore: direction === "before" ? false : prev.hasMoreBefore,
          hasMoreAfter: direction === "after" ? false : prev.hasMoreAfter,
        }));
        return;
      }
      setChartState((prev) => ({
        bars: mergeBars(prev.bars, incomingBars),
        hasMoreBefore: direction === "before" ? Boolean(payload.has_more_before) : prev.hasMoreBefore,
        hasMoreAfter: direction === "after" ? Boolean(payload.has_more_after) : prev.hasMoreAfter,
      }));
    } catch {
      barsDebugLog("edge_request_error", {
        symbol: search.symbol,
        interval: search.interval,
        adjust: search.adjust,
        direction,
        anchorTime,
      });
      return;
    }
  }

  function formatTradeDate(value: string | null | undefined) {
    const raw = String(value || "");
    if (/^\d{8}$/.test(raw)) {
      return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
    }
    return raw || "--";
  }

  const infoTiles = [
    { label: "开盘", value: formatNumber(lastBar?.open) },
    { label: "最高", value: formatNumber(lastBar?.high) },
    { label: "最低", value: formatNumber(lastBar?.low) },
    { label: "成交量", value: formatCompact(symbolLiveQuote?.vol ?? activeWatchItem?.vol ?? lastBar?.volume) },
    { label: "市值", value: formatCompact(fundamentals.market_cap || fundamentals.marketCap) },
    { label: "PE", value: formatNumber(fundamentals.pe) },
    { label: "PB", value: formatNumber(fundamentals.pb) },
    { label: "ROE", value: formatNumber(fundamentals.roe) },
  ];

  return (
    <div className="flex h-dvh min-h-0 flex-col overflow-hidden bg-[var(--bg)] text-[var(--text)]">
      <header className="relative z-40 border-b border-[var(--border)] bg-[#101318]">
        <div className="flex h-12 items-center gap-3 px-3">
          <GlobalAppMenu className="shrink-0" />

          <SymbolSearchInput
            value={searchInput}
            onValueChange={setSearchInput}
            onSubmitSymbol={async (symbol) => {
              const normalized = normalizeTerminalSymbol(symbol);
              if (!normalized || normalized === search.symbol) {
                return;
              }
              patchSearch({ symbol: normalized });
            }}
            className="min-w-[180px] max-w-[240px] flex-1"
            inputClassName="h-8 rounded-md border-[var(--border)] bg-[#0c0f14] pl-9 text-sm"
            dropdownClassName="z-[90]"
            placeholder="代码/名称"
            maxResults={8}
          />

          <div className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] p-1">
            {INTERVAL_OPTIONS.map((option) => (
              <button
                key={option}
                className={`rounded px-1.5 py-1 text-[11px] ${search.interval === option ? "bg-[#2a2f37] text-white" : "text-[var(--muted)] hover:bg-white/5"}`}
                onClick={() => patchSearch({ interval: option })}
              >
                {option}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] p-1">
            {ADJUST_OPTIONS.map((option) => (
              <button
                key={option}
                className={`rounded px-1.5 py-1 text-[11px] ${search.adjust === option ? "bg-[#2a2f37] text-white" : "text-[var(--muted)] hover:bg-white/5"}`}
                onClick={() => patchSearch({ adjust: option })}
              >
                {formatAdjustLabel(option)}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] p-1">
            {INDICATORS.map((indicator) => {
              const active = search.indicators.includes(indicator);
              return (
                <button
                  key={indicator}
                  className={`rounded px-1.5 py-1 text-[11px] ${active ? "bg-[#2a2f37] text-white" : "text-[var(--muted)] hover:bg-white/5"}`}
                  onClick={() => {
                    const next = active ? search.indicators.filter((item) => item !== indicator) : [...search.indicators, indicator];
                    patchSearch({ indicators: next });
                  }}
                >
                  {indicator.toUpperCase()}
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              className="flex h-8 items-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-[var(--muted)] hover:text-slate-100"
              onClick={() => barsQuery.refetch()}
            >
              <RefreshCw className="size-3.5" />
              刷新
            </button>
            <div className="hidden text-[11px] text-[var(--muted)] 2xl:block">
              行情 {marketConnection} / {lastMarketUpdate || "--"}
            </div>
            <Link to="/profile" className="flex items-center gap-2 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 py-1.5 hover:bg-white/5">
              <UserAvatar avatarUrl={currentUser?.avatar_url} displayName={displayUserName} />
              <span className="hidden max-w-[96px] truncate text-sm text-slate-100 xl:block">{displayUserName}</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_340px]">
        <main className="flex min-w-0 flex-col border-r border-[var(--border)] bg-[#0f1115]">
          <div className="terminal-scrollbar flex h-11 items-center gap-4 overflow-x-auto border-b border-[var(--border)] bg-[#12161c] px-3 text-[11px] text-[var(--muted)]">
            <div className="flex min-w-0 items-center gap-2">
              <ContextLabel label="列表" value={currentWatchlist?.name || "Watchlist"} />
              <ContextChip>{search.symbol}</ContextChip>
              <span className="truncate text-slate-300">{displayName}</span>
            </div>

            <div className="flex items-center gap-2">
              <ContextChip>周期 {search.interval}</ContextChip>
              <ContextChip>复权 {formatAdjustLabel(search.adjust)}</ContextChip>
              <ContextChip>指标 {search.indicators.length ? search.indicators.map((item) => item.toUpperCase()).join(" / ") : "NONE"}</ContextChip>
              <ContextChip>{watchRows.length} 标的</ContextChip>
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-2">
              <ContextChip tone={marketConnection === "connected" ? "positive" : "neutral"}>
                行情 {marketConnection}
              </ContextChip>
              <ContextChip>更新 {lastMarketUpdate || "--"}</ContextChip>
              <ContextChip>API {apiLatencyMs == null ? "--" : `${apiLatencyMs}ms`}</ContextChip>
              <ContextChip>交易日 {formatTradeDate(tradeDateValue)}</ContextChip>
            </div>
          </div>

          <div className="flex h-14 items-center justify-between border-b border-[var(--border)] px-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-semibold text-slate-100">{displayName}</span>
                <span className="rounded bg-[#1b2028] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">{search.symbol}</span>
              </div>
              <div className={`mt-1 flex items-center gap-2 text-xs ${isUp ? "text-[var(--up)]" : "text-[var(--down)]"}`}>
                <span className="text-[22px] font-semibold leading-none">{formatNumber(priceValue)}</span>
                <span>{absoluteChange != null ? `${absoluteChange >= 0 ? "+" : ""}${formatNumber(absoluteChange)}` : "--"}</span>
                <span>{percentChange != null ? `${percentChange >= 0 ? "+" : ""}${formatNumber(percentChange)}%` : "--"}</span>
              </div>
            </div>

            <div className="flex items-center gap-4 text-[11px] text-[var(--muted)]">
              <div className="flex items-center gap-3">
                <span>O {formatNumber(lastBar?.open)}</span>
                <span>H {formatNumber(lastBar?.high)}</span>
                <span>L {formatNumber(lastBar?.low)}</span>
                <span>C {formatNumber(lastBar?.close)}</span>
              </div>
              <Link
                to="/wyckoff"
                search={{
                  symbol: search.symbol,
                  interval: search.interval as "D" | "W" | "M",
                  adjust: search.adjust as "none" | "qfq" | "hfq",
                  watchlistId: search.watchlistId,
                  leftPanel: search.leftPanel,
                  rightPanel: search.rightPanel,
                  layoutPreset: search.layoutPreset,
                  limit: 420,
                }}
                target="_blank"
                className="inline-flex h-8 items-center gap-1 rounded border border-[var(--border)] bg-[#111924] px-2 text-xs text-slate-200 hover:bg-white/5"
              >
                <BarChart3 className="size-3.5" />
                威科夫
              </Link>
              <button
                className="inline-flex h-8 items-center gap-1 rounded border border-[var(--border)] bg-[#111924] px-2 text-xs text-slate-200 hover:bg-white/5"
                onClick={() => setAddIntoWatchlistOpen(true)}
              >
                <Plus className="size-3.5" />
                加入自选
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1">
            <MarketChart
              bars={chartState.bars}
              indicators={search.indicators}
              hasMoreBefore={chartState.hasMoreBefore}
              hasMoreAfter={chartState.hasMoreAfter}
              datasetKey={barsDatasetKey}
              onNeedMoreData={handleLoadMoreBars}
            />
          </div>
        </main>

        <aside className="grid min-h-0 grid-rows-[minmax(0,2fr)_minmax(0,1fr)] bg-[#101318]">
          <WatchlistWorkspace
            watchlists={watchlistMeta}
            selectedWatchlistId={selectedWatchlistId}
            items={watchRows}
            activeSymbol={search.symbol}
            viewMode={watchlistView}
            maxListsPerUser={maxListsPerUser}
            loading={watchlistItemsQuery.isLoading || watchlistsQuery.isLoading}
            sortState={currentSortState}
            onSelectWatchlist={(watchlistId) => patchSearch({ watchlistId })}
            onSelectSymbol={(symbol) => patchSearch({ symbol })}
            onViewModeChange={(view) => {
              if (view === "compact" || view === "table") {
                setWatchlistView(view);
              }
            }}
            onSortChange={(sort) => void handlePersistSort(sort)}
            onCreateWatchlist={(name) => handleCreateWatchlist(name)}
            onCopyWatchlist={(watchlistId, name) => handleCopyWatchlist(watchlistId, name)}
            onRenameWatchlist={(watchlistId, name) => handleRenameWatchlist(watchlistId, name)}
            onDeleteWatchlist={(watchlistId) => handleDeleteWatchlist(watchlistId)}
            onAddSymbol={(symbol) => handleAddItemToWatchlist(symbol)}
            onPinToggle={(item, pinned) => handlePatchWatchlistItem(item.id, { pinned })}
            onSetRowColor={(item, color) => handlePatchWatchlistItem(item.id, { row_color: color })}
            onRemoveItem={(item) => handleRemoveWatchlistItem(item.id)}
          />

          <section className="terminal-scrollbar min-h-0 overflow-y-auto px-4 py-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-100">{displayName}</div>
                <div className="mt-1 text-[11px] text-[var(--muted)]">{search.symbol} · 最近交易日 {formatTradeDate(tradeDateValue)}</div>
              </div>
              <div className="text-right">
                <button
                  className="mb-2 inline-flex h-7 items-center gap-1 rounded border border-[var(--border)] bg-[#111924] px-2 text-[11px] text-slate-200 hover:bg-white/5"
                  onClick={() => setAddIntoWatchlistOpen(true)}
                >
                  管理自选
                </button>
                <div className={isUp ? "text-[var(--up)]" : "text-[var(--down)]"}>
                  <div className="text-[28px] font-semibold leading-none">{formatNumber(priceValue)}</div>
                  <div className="mt-1 text-xs">{percentChange != null ? `${percentChange >= 0 ? "+" : ""}${formatNumber(percentChange)}%` : "--"}</div>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-x-5 gap-y-3 text-sm">
              {infoTiles.map((tile) => (
                <Metric key={tile.label} label={tile.label} value={tile.value} />
              ))}
            </div>

            <div className="mt-4 space-y-2">
              <div className="text-[11px] uppercase tracking-wide text-[var(--muted)]">所属板块</div>
              <div className="flex flex-wrap gap-2">
                {boardItems.length ? (
                  boardItems.slice(0, 6).map((item: { board_name?: string; board_type?: string }) => (
                    <span key={`${item.board_name}-${item.board_type}`} className="rounded-full border border-[var(--border-strong)] bg-[#171b21] px-2.5 py-1 text-xs text-slate-300">
                      {item.board_name || "--"}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-[var(--muted)]">暂无板块信息</span>
                )}
              </div>
            </div>

            {Object.keys(factorSummary || {}).length > 0 ? (
              <div className="mt-4 rounded-md border border-[var(--border)] bg-[#171b21] px-3 py-2">
                <div className="mb-2 text-[11px] uppercase tracking-wide text-[var(--muted)]">因子摘要</div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-300">
                  {Object.entries(factorSummary).slice(0, 4).map(([key, value]) => (
                    <div key={key}>
                      <div className="text-[var(--muted)]">{key}</div>
                      <div>{typeof value === "number" ? formatNumber(value) : String(value ?? "--")}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </section>
        </aside>
      </div>

      <AddIntoWatchlistModal
        open={addIntoWatchlistOpen}
        symbol={search.symbol}
        displayName={displayName}
        watchlists={watchlistMeta}
        maxListsPerUser={maxListsPerUser}
        onClose={() => setAddIntoWatchlistOpen(false)}
        onAfterChange={async () => {
          await refreshWatchlistQueries();
        }}
      />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-[var(--muted)]">{label}</div>
      <div className="mt-1 text-sm text-slate-200">{value}</div>
    </div>
  );
}

function ContextChip({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "positive";
}) {
  return (
    <span
      className={`rounded border px-2 py-1 font-mono text-[10px] tracking-wide ${
        tone === "positive"
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
          : "border-[var(--border)] bg-[#171b21] text-slate-300"
      }`}
    >
      {children}
    </span>
  );
}

function ContextLabel({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="uppercase tracking-wide text-[var(--muted)]">{label}</span>
      <span className="truncate text-slate-200">{value}</span>
    </span>
  );
}

function UserAvatar({ avatarUrl, displayName }: { avatarUrl?: string | null; displayName: string }) {
  const initials = displayName.trim().slice(0, 2).toUpperCase() || "U";
  if (avatarUrl) {
    return <img src={avatarUrl} alt={displayName} className="size-7 rounded-full object-cover ring-1 ring-white/10" />;
  }
  return <div className="flex size-7 items-center justify-center rounded-full bg-[#1b2430] text-[11px] font-semibold text-slate-100 ring-1 ring-white/10">{initials}</div>;
}

function normalizeBarsPayload(raw: unknown[]) {
  return raw
    .map((item) => {
      const typed = item as Record<string, unknown>;
      return {
        time: normalizeChartTime(typed.time || typed.trade_date || typed.date || ""),
        open: Number(typed.open || 0),
        high: Number(typed.high || 0),
        low: Number(typed.low || 0),
        close: Number(typed.close || 0),
        volume: Number(typed.volume || typed.vol || 0),
      };
    })
    .filter((item) => item.time)
    .sort((left, right) => left.time.localeCompare(right.time));
}

function mergeBars(currentBars: TerminalBar[], nextBars: TerminalBar[]) {
  const merged = new Map<string, TerminalBar>();
  [...currentBars, ...nextBars].forEach((bar) => {
    merged.set(bar.time, bar);
  });
  return Array.from(merged.values()).sort((left, right) => left.time.localeCompare(right.time));
}

function normalizeChartTime(raw: unknown) {
  const value = String(raw || "");
  if (/^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  }
  return value;
}

function toTradeDateDigits(raw: string) {
  return String(raw).replaceAll("-", "").slice(0, 8);
}

function toFiniteNumber(raw: unknown) {
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function normalizeLiveQuote(raw: unknown, fallbackTradeDate?: unknown): LiveQuoteSnapshot | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const typed = raw as Record<string, unknown>;
  const close = toFiniteNumber(typed.close);
  if (close == null) {
    return null;
  }
  let pctChg = toFiniteNumber(typed.pct_chg);
  let preClose = toFiniteNumber(typed.pre_close);
  let change = toFiniteNumber(typed.change);
  if (change == null && preClose != null) {
    change = close - preClose;
  }
  if (pctChg == null && preClose != null && preClose !== 0 && change != null) {
    pctChg = (change / preClose) * 100;
  }
  if (preClose == null && change != null) {
    preClose = close - change;
  }

  const tradeDateDigits = toTradeDateDigits(String(typed.trade_date || typed.time || fallbackTradeDate || ""));
  return {
    close,
    pct_chg: pctChg,
    pre_close: preClose,
    change,
    trade_date: tradeDateDigits || null,
    vol: toFiniteNumber(typed.vol || typed.volume),
    amount: toFiniteNumber(typed.amount),
    source: typed.source == null ? null : String(typed.source || ""),
  };
}

function readTerminalSessionFromLocal() {
  try {
    const raw = window.localStorage.getItem(TERMINAL_SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return parseTerminalSession(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeTerminalSessionToLocal(sessionState: TerminalSessionState) {
  try {
    window.localStorage.setItem(TERMINAL_SESSION_STORAGE_KEY, JSON.stringify(sessionState));
  } catch {
    return;
  }
}

function parseTerminalSession(raw: unknown): TerminalSessionState | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const candidate = raw as Record<string, unknown>;
  const interval = parseInterval(candidate.interval);
  const adjust = parseAdjust(candidate.adjust);
  return {
    interval,
    adjust,
    indicators: Array.isArray(candidate.indicators) ? candidate.indicators.map((item) => String(item)) : ["ma"],
    layoutPreset: String(candidate.layoutPreset || "terminal"),
    leftPanel: String(candidate.leftPanel || "expanded"),
    rightPanel: String(candidate.rightPanel || "expanded"),
    watchlistView: candidate.watchlistView === "compact" ? "compact" : "table",
  };
}

function normalizeWatchlistSortField(raw: string) {
  const value = String(raw || "").trim().toLowerCase();
  if (!value) {
    return "pct_chg";
  }
  if (value === "display_name" || value === "name_cache") {
    return "name";
  }
  if (value === "volume") {
    return "vol";
  }
  if (value === "turnover") {
    return "amount";
  }
  return value;
}

function parseInterval(raw: unknown): (typeof INTERVAL_OPTIONS)[number] {
  const value = String(raw || "D") as (typeof INTERVAL_OPTIONS)[number];
  return INTERVAL_OPTIONS.includes(value) ? value : "D";
}

function parseAdjust(raw: unknown): (typeof ADJUST_OPTIONS)[number] {
  const value = String(raw || "none") as (typeof ADJUST_OPTIONS)[number];
  return ADJUST_OPTIONS.includes(value) ? value : "none";
}

function formatAdjustLabel(value: (typeof ADJUST_OPTIONS)[number]) {
  if (value === "none") {
    return "不复权";
  }
  if (value === "hfq") {
    return "后复权";
  }
  return "前复权";
}
